import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Lock, Shield, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth, type User } from "../../auth/AuthContext";
import { updateProfile } from "../../api/auth";

interface ProfileFormProps {
  user: User;
  onCompletionChange?: (pct: number) => void;
}

const AVATAR_BG: Record<string, string> = {
  admin: "rgba(239,68,68,0.2)",
  analyst: "rgba(168,85,247,0.2)",
  client: "rgba(0,102,224,0.2)",
};
const AVATAR_COLOR: Record<string, string> = {
  admin: "#ef4444",
  analyst: "#a855f7",
  client: "#818cf8",
};

const JOB_TITLE_SUGGESTIONS = [
  "Security Analyst",
  "CISO",
  "SOC Lead",
  "IT Administrator",
  "Threat Intelligence Lead",
];

export default function ProfileForm({ user, onCompletionChange }: ProfileFormProps) {
  const { refreshUser } = useAuth();

  const [firstName, setFirstName] = useState(user.first_name || "");
  const [lastName, setLastName] = useState(user.last_name || "");
  const [organization, setOrganization] = useState(user.organization || "");
  const [jobTitle, setJobTitle] = useState(user.job_title || "");

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);

  // Sync state if user prop changes
  useEffect(() => {
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setOrganization(user.organization || "");
    setJobTitle(user.job_title || "");
    setIsDirty(false);
  }, [user]);

  // Check if form values changed
  useEffect(() => {
    const dirty =
      firstName.trim() !== (user.first_name || "").trim() ||
      lastName.trim() !== (user.last_name || "").trim() ||
      organization.trim() !== (user.organization || "").trim() ||
      jobTitle.trim() !== (user.job_title || "").trim();
    setIsDirty(dirty);
  }, [firstName, lastName, organization, jobTitle, user]);

  // Completion calculation (filled fields out of 4)
  const { completionPct, missingFields } = useMemo(() => {
    let count = 0;
    const missing: string[] = [];

    if (firstName.trim()) count++;
    else missing.push("first name");

    if (lastName.trim()) count++;
    else missing.push("last name");

    if (organization.trim()) count++;
    else missing.push("organization");

    if (jobTitle.trim()) count++;
    else missing.push("job title");

    const pct = Math.round((count / 4) * 100);
    return { completionPct: pct, missingFields: missing };
  }, [firstName, lastName, organization, jobTitle]);

  // Notify parent sidebar of completion percentage
  useEffect(() => {
    if (onCompletionChange) {
      onCompletionChange(completionPct);
    }
  }, [completionPct, onCompletionChange]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved profile changes. Are you sure you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Form Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (firstName.trim().length > 50) {
      newErrors.first_name = "First name must not exceed 50 characters.";
    }

    if (lastName.trim().length > 50) {
      newErrors.last_name = "Last name must not exceed 50 characters.";
    }

    if (organization.trim().length > 100) {
      newErrors.organization = "Organization must not exceed 100 characters.";
    }

    if (jobTitle.trim().length > 80) {
      newErrors.job_title = "Job title must not exceed 80 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReset = () => {
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setOrganization(user.organization || "");
    setJobTitle(user.job_title || "");
    setErrors({});
    setIsDirty(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !isDirty) return;

    setSaving(true);
    try {
      await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        organization: organization.trim(),
        job_title: jobTitle.trim(),
      });

      // Refresh AuthContext user so header & greeting update immediately
      await refreshUser();
      setIsDirty(false);
      toast.success("Profile details saved successfully.");
    } catch (err: any) {
      console.error("Failed to save profile:", err);
      const msg = err.response?.data?.detail || "Failed to update profile details.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // User avatar initial
  const role = user.role || "client";
  const initial = (firstName || user.name || user.email).trim().charAt(0).toUpperCase();
  const displayName = [firstName, lastName].filter(Boolean).join(" ") || user.name || user.email.split("@")[0];

  // Format dates if available
  const createdDate = user.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  const lastLoginDate = user.last_login ? new Date(user.last_login).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* HEADER DOSSIER CARD */}
      <div className="profile-header-card">
        <div className="profile-header-left">
          <div
            className="profile-avatar-large"
            style={{
              background: AVATAR_BG[role] ?? AVATAR_BG.client,
              color: AVATAR_COLOR[role] ?? AVATAR_COLOR.client,
            }}
          >
            {initial}
          </div>

          <div className="profile-user-details">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 className="profile-user-name">{displayName}</h2>
              <span
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  padding: "3px 8px",
                  borderRadius: 4,
                  textTransform: "uppercase",
                  background: AVATAR_BG[role] ?? AVATAR_BG.client,
                  color: AVATAR_COLOR[role] ?? AVATAR_COLOR.client,
                  border: `1px solid ${AVATAR_COLOR[role]}40`,
                }}
                title="Your access level is managed by an administrator"
              >
                {role.toUpperCase()}
              </span>
            </div>

            <div className="profile-user-email-row">
              <span>{user.email}</span>
            </div>

            {(createdDate || lastLoginDate) && (
              <div className="profile-meta-row">
                {createdDate && <span>Member since {createdDate}</span>}
                {createdDate && lastLoginDate && <span>•</span>}
                {lastLoginDate && <span>Last login {lastLoginDate}</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* COMPLETION PROGRESS CARD (Fades out when 100%) */}
      {completionPct < 100 && (
        <div className="profile-progress-card">
          <div className="profile-progress-left">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "rgba(0, 102, 224, 0.15)",
                color: "var(--dash-indigo, #6366F1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: "0.85rem",
              }}
            >
              {completionPct}%
            </div>
            <div>
              <div className="profile-progress-title">Complete your profile</div>
              <div className="profile-progress-sub">
                Add your {missingFields.join(" and ")} to reach 100% completion.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MAIN FORM CARD */}
      <form onSubmit={handleSubmit} className="profile-form-card" noValidate>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)", margin: 0 }}>
              Personal Information
            </h3>
            <p style={{ fontSize: "0.78rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 2, margin: 0 }}>
              Update your personal details and organization role
            </p>
          </div>
        </div>

        {/* FORM FIELDS GRID */}
        <div className="profile-form-grid">
          {/* FIRST NAME */}
          <div className="form-group">
            <label className="form-label" htmlFor="first_name">
              <span>First Name</span>
              <span className="form-helper-text">{firstName.length}/50</span>
            </label>
            <input
              id="first_name"
              type="text"
              className={`form-input${errors.first_name ? " error" : ""}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="e.g. Digvijay"
              maxLength={50}
            />
            {errors.first_name && <div className="form-error-text">{errors.first_name}</div>}
          </div>

          {/* LAST NAME */}
          <div className="form-group">
            <label className="form-label" htmlFor="last_name">
              <span>Last Name</span>
              <span className="form-helper-text">{lastName.length}/50</span>
            </label>
            <input
              id="last_name"
              type="text"
              className={`form-input${errors.last_name ? " error" : ""}`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="e.g. Kesare"
              maxLength={50}
            />
            {errors.last_name && <div className="form-error-text">{errors.last_name}</div>}
          </div>

          {/* ORGANIZATION */}
          <div className="form-group">
            <label className="form-label" htmlFor="organization">
              <span>Organization / Company</span>
              <span className="form-helper-text">{organization.length}/100</span>
            </label>
            <input
              id="organization"
              type="text"
              className={`form-input${errors.organization ? " error" : ""}`}
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="e.g. Threatactix Security"
              maxLength={100}
            />
            {errors.organization && <div className="form-error-text">{errors.organization}</div>}
          </div>

          {/* JOB TITLE WITH QUICK SUGGESTION PILLS */}
          <div className="form-group">
            <label className="form-label" htmlFor="job_title">
              <span>Job Title</span>
              <span className="form-helper-text">{jobTitle.length}/80</span>
            </label>
            <input
              id="job_title"
              type="text"
              className={`form-input${errors.job_title ? " error" : ""}`}
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Security Analyst"
              maxLength={80}
            />
            {errors.job_title && <div className="form-error-text">{errors.job_title}</div>}

            {/* Quick Suggestions */}
            <div className="suggestion-pills">
              {JOB_TITLE_SUGGESTIONS.map((title) => (
                <button
                  key={title}
                  type="button"
                  className="suggestion-pill-btn"
                  onClick={() => setJobTitle(title)}
                >
                  + {title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* READ ONLY SYSTEM FIELDS */}
        <div className="profile-form-grid" style={{ paddingTop: 16, borderTop: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))" }}>
          {/* READ ONLY EMAIL */}
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Lock size={12} /> Email Address
              </span>
              <span className="form-helper-text">System Account</span>
            </label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={user.email}
              disabled
              readOnly
            />
            <div className="form-helper-text" style={{ marginTop: 2 }}>
              Contact an administrator to change your registered email.
            </div>
          </div>

          {/* READ ONLY ROLE */}
          <div className="form-group">
            <label className="form-label" htmlFor="role">
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Shield size={12} /> Account Role
              </span>
              <span className="form-helper-text">Managed</span>
            </label>
            <input
              id="role"
              type="text"
              className="form-input"
              value={role.toUpperCase()}
              disabled
              readOnly
            />
            <div className="form-helper-text" style={{ marginTop: 2 }}>
              Your access level is managed by an administrator.
            </div>
          </div>
        </div>

        {/* FORM ACTION BUTTONS */}
        <div className="form-actions-row">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleReset}
            disabled={!isDirty || saving}
          >
            Reset
          </button>

          <button
            type="submit"
            className="btn-primary"
            disabled={!isDirty || saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Save Changes</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
